const FILE_MARKER = '__bnsFile';

const FILE_LIST_MARKER = '__bnsFileList';



export const IMAGE_SPEC_TITLES = new Set(['조절기 이미지', '제어기 이미지']);

export const FIRMWARE_SPEC_TITLE = '펌웨어 파일';

export const WIRING_DIAGRAM_SPEC_TITLE = '결선도/발주서/체크리스트';

/** 저장된 구 제목(표시·매칭 호환) */

export const WIRING_DIAGRAM_SPEC_TITLE_LEGACY = '결선도 파일';

export const WIRING_DIAGRAM_MAX_UPLOAD_BYTES = 20 * 1024 * 1024; // 20MB

const NOTE_SPEC_TITLE = '비고';



const trimTitle = (title = '') => String(title || '').trim();



export const isImageSpecTitle = (title = '') => IMAGE_SPEC_TITLES.has(trimTitle(title));

export const isFirmwareSpecTitle = (title = '') => trimTitle(title) === FIRMWARE_SPEC_TITLE;

export const isWiringDiagramSpecTitle = (title = '') => {

    const t = trimTitle(title);

    return t === WIRING_DIAGRAM_SPEC_TITLE || t === WIRING_DIAGRAM_SPEC_TITLE_LEGACY;

};



export const normalizeWiringDiagramSpecTitle = (title = '') => (

    isWiringDiagramSpecTitle(title) ? WIRING_DIAGRAM_SPEC_TITLE : trimTitle(title)

);

export const isUploadSpecTitle = (title = '') => (

    isImageSpecTitle(title) || isFirmwareSpecTitle(title) || isWiringDiagramSpecTitle(title)

);



/** 사양 객체가 파일 업로드 필드인지(제목 또는 inputType) */

export const isSpecFileUploadField = (spec) => {

    if (!spec) {

        return false;

    }

    if (isUploadSpecTitle(spec.title)) {

        return true;

    }

    return String(spec?.inputType || '').trim().toLowerCase() === 'file';

};



/** 구 데이터에 결선도 행이 없으면 비고 앞에 삽입 */

export const ensureWiringDiagramSpecInSpecs = (specs) => {

    if (!Array.isArray(specs)) {

        return specs;

    }

    if (specs.some((s) => isWiringDiagramSpecTitle(s?.title))) {

        return specs.map((s) => (

            isWiringDiagramSpecTitle(s?.title)

                ? { ...s, title: WIRING_DIAGRAM_SPEC_TITLE }

                : s

        ));

    }

    const noteIdx = specs.findIndex((s) => trimTitle(s?.title) === NOTE_SPEC_TITLE);

    const row = { title: WIRING_DIAGRAM_SPEC_TITLE, fullWidth: true, details: '' };

    if (noteIdx === -1) {

        return [...specs, row];

    }

    const next = [...specs];

    next.splice(noteIdx, 0, row);

    return next;

};



const inferKindFromMime = (mimeType = '') => {

    const mime = String(mimeType || '').toLowerCase();

    if (mime.startsWith('image/')) {

        return 'image';

    }

    if (mime === 'application/pdf') {

        return 'wiringPdf';

    }

    return 'firmware';

};



const inferKindFromFileName = (name = '') => {

    const lower = String(name || '').toLowerCase();

    if (/\.(webp|png|jpe?g|gif|bmp|svg)$/i.test(lower)) {

        return 'image';

    }

    if (/\.pdf$/i.test(lower)) {

        return 'wiringPdf';

    }

    if (/\.hex$/i.test(lower)) {

        return 'firmware';

    }

    return '';

};



const normalizeParsedFileEntry = (file, specTitle = '') => {

    if (!file || typeof file !== 'object') {

        return null;

    }

    const kind = inferSpecFileKind(file, specTitle);

    return {

        ...file,

        [FILE_MARKER]: true,

        kind,

        name: String(file.name || '').trim(),

        mimeType: String(file.mimeType || '').trim(),

        dataUrl: String(file.dataUrl || '').trim(),

        description: String(file.description || '').trim(),

    };

};



const parseRawDataUrlFile = (value) => {

    const trimmed = String(value || '').trim();

    if (!trimmed.startsWith('data:')) {

        return null;

    }

    const mimeMatch = trimmed.match(/^data:([^;,]+)/i);

    const mimeType = mimeMatch ? mimeMatch[1] : '';

    const kind = inferKindFromMime(mimeType);

    const ext = mimeType.split('/')[1] || 'bin';

    return normalizeParsedFileEntry({

        kind,

        name: `file.${ext}`,

        mimeType,

        dataUrl: trimmed,

        description: '',

    });

};



const parsePlainFileNameDetails = (value) => {

    const trimmed = String(value || '').trim();

    if (!trimmed || trimmed.startsWith('{') || trimmed.startsWith('data:')) {

        return null;

    }

    const kind = inferKindFromFileName(trimmed);

    if (!kind) {

        return null;

    }

    return normalizeParsedFileEntry({

        kind,

        name: trimmed,

        mimeType: '',

        dataUrl: '',

        description: '',

    });

};



export const parseSpecFileValue = (value) => {

    if (!value || typeof value !== 'string') {

        return null;

    }



    const rawDataUrl = parseRawDataUrlFile(value);

    if (rawDataUrl) {

        return rawDataUrl;

    }



    try {

        const parsed = JSON.parse(value);

        if (parsed && parsed[FILE_MARKER] === true) {

            return normalizeParsedFileEntry(parsed);

        }

        return null;

    } catch (error) {

        return null;

    }

};



export const createSpecFileValue = ({ kind, name, mimeType, dataUrl, description }) => JSON.stringify({

    [FILE_MARKER]: true,

    kind,

    name: name || '',

    mimeType: mimeType || '',

    dataUrl: dataUrl || '',

    description: description || '',

});



export const parseSpecFileListValue = (value, specTitle = '') => {
    if (value && typeof value === 'object') {
        if (Array.isArray(value.files)) {
            return value.files
                .map((file) => normalizeParsedFileEntry(file, specTitle))
                .filter(Boolean);
        }
        if (value[FILE_MARKER] === true) {
            const file = normalizeParsedFileEntry(value, specTitle);
            return file ? [file] : [];
        }
        return [];
    }

    if (!value || typeof value !== 'string') {
        return [];
    }



    const trimmed = String(value).trim();

    if (!trimmed) {

        return [];

    }



    const rawDataUrl = parseRawDataUrlFile(trimmed);

    if (rawDataUrl) {

        return [rawDataUrl];

    }



    try {

        const parsed = JSON.parse(trimmed);

        if (parsed && parsed[FILE_LIST_MARKER] === true && Array.isArray(parsed.files)) {

            return parsed.files

                .map((file) => normalizeParsedFileEntry(file, specTitle))

                .filter(Boolean);

        }

        if (parsed && Array.isArray(parsed.files)) {

            return parsed.files

                .map((file) => normalizeParsedFileEntry(file, specTitle))

                .filter(Boolean);

        }

        if (parsed && parsed[FILE_MARKER] === true) {

            const file = normalizeParsedFileEntry(parsed, specTitle);

            return file ? [file] : [];

        }

        if (parsed && typeof parsed === 'object' && String(parsed.name || '').trim()) {

            const file = normalizeParsedFileEntry(parsed, specTitle);

            if (file) {

                return [file];

            }

        }

    } catch (error) {

        const plain = parsePlainFileNameDetails(trimmed);

        return plain ? [plain] : [];

    }



    const plain = parsePlainFileNameDetails(trimmed);

    return plain ? [plain] : [];

};



export const createSpecFileListValue = ({ files }) => JSON.stringify({

    [FILE_LIST_MARKER]: true,

    files: Array.isArray(files) ? files : [],

});



const getUploadSpecMergeKey = (title = '') => String(normalizeWiringDiagramSpecTitle(title) || '').trim();



/** 업로드 사양 파일 kind 추론(구 데이터 호환) */

export const inferSpecFileKind = (file, specTitle = '') => {

    const explicit = String(file?.kind || '').trim();

    if (explicit) {

        return explicit;

    }

    const mime = String(file?.mimeType || '').toLowerCase();

    if (mime.startsWith('image/')) {

        return 'image';

    }

    if (mime === 'application/pdf') {

        return 'wiringPdf';

    }

    const fromName = inferKindFromFileName(file?.name);

    if (fromName) {

        return fromName;

    }

    if (isImageSpecTitle(specTitle)) {

        return 'image';

    }

    if (isFirmwareSpecTitle(specTitle)) {

        return 'firmware';

    }

    if (isWiringDiagramSpecTitle(specTitle)) {

        return 'wiringPdf';

    }

    return explicit;

};



const normalizeSpecDetailsString = (details) => {
    if (details == null) {
        return '';
    }
    if (typeof details === 'string') {
        return details;
    }
    return JSON.stringify(details);
};

const isFileSpecForAssetMerge = (spec) => (
    isUploadSpecTitle(spec?.title)
    || String(spec?.inputType || '').trim().toLowerCase() === 'file'
);

/** asset API 응답 — 사양 제목별로만 조회 (같은 파일명이 여러 리스트에 있어도 섞이지 않음) */
export const buildAssetLookupFromSpecs = (assetSpecs) => {
    const byTitle = new Map();

    (assetSpecs || []).forEach((spec) => {
        const title = getUploadSpecMergeKey(spec?.title);
        if (isFileSpecForAssetMerge(spec)) {
            byTitle.set(title, spec);
        }
    });

    return { byTitle };
};

const findAssetFileByName = (assetSpec, specTitle, fileName) => {
    const target = String(fileName || '').trim();
    if (!target || !assetSpec) {
        return null;
    }
    return parseSpecFileListValue(assetSpec.details, specTitle).find(
        (file) => String(file?.name || '').trim() === target && String(file?.dataUrl || '').trim(),
    ) || null;
};

const mergeFilesWithAssetSpec = (baseFiles, assetSpec, specTitle) => (
    (baseFiles || []).map((file) => {
        if (String(file?.dataUrl || '').trim()) {
            return file;
        }
        const name = String(file?.name || '').trim();
        const fromAsset = name ? findAssetFileByName(assetSpec, specTitle, name) : null;
        if (fromAsset) {
            return { ...file, ...fromAsset, name: file.name || fromAsset.name };
        }
        return file;
    })
);

const enrichSpecDetailsWithAssetLookup = (details, specTitle, { byTitle }) => {
    const title = getUploadSpecMergeKey(specTitle);
    const asset = byTitle.get(title);

    if (asset?.details != null) {
        const assetFiles = parseSpecFileListValue(asset.details, specTitle);
        if (assetFiles.some((file) => String(file?.dataUrl || '').trim())) {
            const baseFiles = parseSpecFileListValue(details, specTitle);
            if (baseFiles.length === 0) {
                return normalizeSpecDetailsString(asset.details);
            }
            const enrichedFiles = mergeFilesWithAssetSpec(baseFiles, asset, specTitle);
            if (enrichedFiles.some((file) => String(file?.dataUrl || '').trim())) {
                return createSpecFileListValue({ files: enrichedFiles });
            }
            return normalizeSpecDetailsString(asset.details);
        }
    }

    const plainName = typeof details === 'string' ? String(details).trim() : '';
    if (plainName && !plainName.startsWith('{') && !plainName.startsWith('data:')) {
        const fromAsset = findAssetFileByName(asset, specTitle, plainName);
        if (fromAsset) {
            return createSpecFileListValue({ files: [{ ...fromAsset, name: plainName }] });
        }
    }

    const baseFiles = parseSpecFileListValue(details, specTitle);
    if (baseFiles.length > 0 && asset) {
        const enrichedFiles = mergeFilesWithAssetSpec(baseFiles, asset, specTitle);
        if (enrichedFiles.some((file) => String(file?.dataUrl || '').trim())) {
            return createSpecFileListValue({ files: enrichedFiles });
        }
    }

    return normalizeSpecDetailsString(details);
};

/** 승인 목록(경량) 응답에 이어붙일 업로드 사양 파일 payload */
export const mergeProductSpecsAssets = (baseSpecs, assetSpecs) => {
    if (!Array.isArray(baseSpecs)) {
        return baseSpecs;
    }

    const lookup = buildAssetLookupFromSpecs(assetSpecs);

    return baseSpecs.map((spec) => {
        if (!isFileSpecForAssetMerge(spec)) {
            return spec;
        }
        return {
            ...spec,
            details: enrichSpecDetailsWithAssetLookup(spec.details, spec.title, lookup),
        };
    });
};

/** 승인 diff(변경전/후)에 파일 dataUrl 병합 — diff에는 파일명만 저장된 경우 대비 */
export const mergeReviewDiffWithAssetSpecs = (changes, assetSpecs) => {
    if (!Array.isArray(changes)) {
        return changes;
    }
    const lookup = buildAssetLookupFromSpecs(assetSpecs);

    return changes.map((change) => {
        const field = String(change?.field || '');
        if (!field.startsWith('spec:')) {
            return change;
        }
        const specTitle = field.slice('spec:'.length);
        if (!isUploadSpecTitle(specTitle)) {
            return change;
        }
        return {
            ...change,
            before: enrichSpecDetailsWithAssetLookup(change.before, specTitle, lookup),
            after: enrichSpecDetailsWithAssetLookup(change.after, specTitle, lookup),
        };
    });
};



export const specFileListNeedsAssetLoad = (value, specTitle = '') => {

    if (value == null) {

        return false;

    }

    if (typeof value === 'object') {

        const files = parseSpecFileListValue(value, specTitle);

        if (files.length === 0) {

            return true;

        }

        return files.some((file) => {

            const kind = inferSpecFileKind(file, specTitle);

            const hasMeta = String(file?.name || '').trim()

                || String(file?.mimeType || '').trim()

                || !!kind;

            return hasMeta && !String(file?.dataUrl || '').trim();

        });

    }

    const trimmed = String(value || '').trim();

    if (!trimmed) {

        return false;

    }

    if (trimmed.startsWith('data:')) {

        return false;

    }

    const files = parseSpecFileListValue(trimmed, specTitle);

    if (files.length === 0) {

        return trimmed.length > 0;

    }

    return files.some((file) => {

        const kind = inferSpecFileKind(file, specTitle);

        const hasMeta = String(file?.name || '').trim()

            || String(file?.mimeType || '').trim()

            || !!kind;

        return hasMeta && !String(file?.dataUrl || '').trim();

    });

};



export const modelSpecsNeedAssetLoad = (model) => {

    if (!model) {

        return false;

    }

    if (model.specsAssetsDeferred) {

        return true;

    }

    return (model.specs || []).some((spec) => (

        (isUploadSpecTitle(spec?.title) || isSpecFileUploadField(spec))

        && specFileListNeedsAssetLoad(spec.details, spec.title)

    ));

};

/** 업로드 파일 dataUrl이 이미 병합되어 표시 가능한지 */
export const modelHasVisibleUploadAssets = (model) => (
    (model?.specs || []).some((spec) => {
        if (!isUploadSpecTitle(spec?.title) && !isSpecFileUploadField(spec)) {
            return false;
        }
        return parseSpecFileListValue(spec.details, spec.title).some(
            (file) => String(file?.dataUrl || '').trim(),
        );
    })
);

const stripFileEntryForList = (file) => {
    if (!file || typeof file !== 'object') {
        return file;
    }
    return { ...file, dataUrl: '' };
};

const stripSpecDetailsForList = (details) => {
    if (details == null) {
        return '';
    }
    if (typeof details === 'object') {
        if (Array.isArray(details.files)) {
            return createSpecFileListValue({ files: details.files.map(stripFileEntryForList) });
        }
        if (details[FILE_MARKER] === true) {
            return createSpecFileValue(stripFileEntryForList(details));
        }
        return JSON.stringify(deepStripHeavyObject(details));
    }
    const trimmed = String(details).trim();
    if (!trimmed) {
        return '';
    }
    if (trimmed.startsWith('data:') && trimmed.length > 200) {
        const raw = parseRawDataUrlFile(trimmed);
        return raw ? createSpecFileListValue({ files: [stripFileEntryForList(raw)] }) : '';
    }
    try {
        const parsed = JSON.parse(trimmed);
        if (parsed && Array.isArray(parsed.files)) {
            return createSpecFileListValue({ files: parsed.files.map(stripFileEntryForList) });
        }
        if (parsed && parsed[FILE_MARKER] === true) {
            return createSpecFileValue(stripFileEntryForList(parsed));
        }
        if (trimmed.length > 8192 && /"dataUrl"\s*:\s*"data:/.test(trimmed)) {
            return trimmed.replace(/"dataUrl"\s*:\s*"data:[^"]*"/gi, '"dataUrl":""');
        }
    } catch (error) {
        return isHeavyListPayloadString(trimmed) ? '' : details;
    }
    return details;
};

const isHeavyListPayloadString = (value = '') => {
    const text = String(value || '');
    return (text.startsWith('data:') && text.length > 200)
        || (text.length > 8192 && /base64/i.test(text));
};

const deepStripHeavyObject = (value) => {
    if (typeof value === 'string') {
        return isHeavyListPayloadString(value) ? '' : value;
    }
    if (Array.isArray(value)) {
        return value.map(deepStripHeavyObject);
    }
    if (!value || typeof value !== 'object') {
        return value;
    }
    const next = { ...value };
    Object.keys(next).forEach((key) => {
        if (/dataurl/i.test(key)) {
            next[key] = '';
            return;
        }
        next[key] = deepStripHeavyObject(next[key]);
    });
    return next;
};

const specDetailsHasHeavyListPayload = (details) => {
    if (details == null) {
        return false;
    }
    if (typeof details === 'object') {
        return /"dataUrl"\s*:\s*"data:/.test(JSON.stringify(details));
    }
    return isHeavyListPayloadString(String(details).trim());
};

/** 서버 strip 누락 시 프론트에서 목록 payload 한 번 더 경량화 */
export const stripListProductRowForFetch = (row) => {
    if (!row || typeof row !== 'object') {
        return row;
    }
    return {
        ...row,
        specs: (row.specs || []).map((spec) => {
            if (!spec || typeof spec !== 'object') {
                return spec;
            }
            const title = trimTitle(spec.title);
            const details = spec.details;
            const heavy = isUploadSpecTitle(title)
                || isSpecFileUploadField(spec)
                || specDetailsHasHeavyListPayload(details);
            if (!heavy) {
                return spec;
            }
            return {
                ...spec,
                title,
                details: stripSpecDetailsForList(details),
            };
        }),
        specsAssetsDeferred: true,
    };
};

/** 목록 API가 아직 이미지/파일 본문을 통째로 내려주는지(구버전 서버) */
export const detectListResponseStillHasEmbeddedAssets = (rows) => {
    if (!Array.isArray(rows)) {
        return false;
    }
    return rows.some((row) => (
        (row.specs || []).some((spec) => specDetailsHasHeavyListPayload(spec?.details))
    ));
};

